import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { HomeIcon, FileIcon, GroupUserIcon, SettingsIcon, CarIcon } from "./icon.utils";

import Layout from "../layouts/Layout";
import DasboardPage from "../pages/Dashboard";
import StockListPage from "../pages/StockList";
import CustomerPage from "../pages/Customer";
import LoginPage from "../pages/Login";
import ProfilePage from "../pages/Profile";
import RolePage from "../pages/Role";
import ModulePage from "../pages/Module";
import PermissionPage from "../pages/Permission";
import RolePermissionPage from "../pages/RolePermissionPage";
import UserPage from "../pages/UserPage";
import SettingsPage from "../pages/Settings";
import Pages from "../pages";
import UnauthorizedPage from "../pages/Unauthorized";
import CarInfoTypePage from "../pages/CarInfoType";
import CarInfoPage from "../pages/CarInfo";
import StockListEditPage from "../pages/StockListEdit";
import StockListAddPage from "../pages/StockListAdd";
import StockListViewPage from "../pages/StockListView";
import CustomerProfilePage from "../pages/CustomerProfile";
import QuotationListPage from "../pages/QuotationList";
import QuotationAddPage from "../pages/QuotationAdd";
import QuotationEditPage from "../pages/QuotationEdit";
import MoneyReceiptListPage from "../pages/MoneyReceiptList";
import MoneyReceiptEditPage from "../pages/MoneyReceiptEdit";
import MoneyReceiptAddPage from "../pages/MoneyReceiptAdd";
import MoneyReceiptViewPage from "../pages/MoneyReceiptView";
import VehicleInventoryAddPage from "../pages/VehicleInventoryAdd";
import VehicleInventoryEditPage from "../pages/VehicleInventoryEdit";
import VehicleShowroomWorksEditPage from "../pages/VehicleShowroomWorksEdit";
import VehicleShowroomWorksAddPage from "../pages/VehicleShowroomWorksAdd";

export const routes = [
  {
    name: "Dashboard",
    route: "/dashboard",
    icon: HomeIcon,
    permissions: ["dashboard:view"]
  },
  {
    name: "Stock List",
    icon: FileIcon,
    route: "/stock-list",
    permissions: ["stock-list:view"]
  },
  {
    name: "Customer",
    icon: GroupUserIcon,
    route: "/customer",
    permissions: ["customer:view"]
  },
  {
    name: "Quotation",
    icon: FileIcon,
    route: "/quotation-list",
    permissions: ["quotation-list:view"]
  },
  {
    name: "Money Receipt",
    icon: FileIcon,
    route: "/money-receipt-list",
    permissions: ["money-receipt-list:view"]
  },
  {
    name: "Car",
    icon: CarIcon,
    route: "/car",
    permissions: ["car:view"],
    children: [
      {
        name: "Car Info",
        route: "/car/car-info",
        permissions: ["car-info:view"]
      },
      {
        name: "Car Info Type",
        route: "/car/car-info-type",
        permissions: ["car-info-type:view"]
      }
    ]
  },
  {
    name: "Settings",
    icon: SettingsIcon,
    route: "/settings",
    permissions: ["settings:view"],
    children: [
      {
        name: "User",
        route: "/settings/user",
        permissions: ["user:view"]
      },
      {
        name: "Role",
        route: "/settings/role",
        permissions: ["role:view"]
      },
      {
        name: "Module",
        route: "/settings/module",
        permissions: ["module:view"]
      },
      {
        name: "Permission",
        route: "/settings/permission",
        permissions: ["permission:view"]
      }
    ]
  }
];

export function getAllRouteData() {
  return routes;
}

export function useHasPermission() {
  const { isAuth, user } = useSelector((state) => state.auth);
  // Check if the user has permission to access the current route
  const hasPermission = (requiredPermissions) => {
    if (requiredPermissions?.length && user?.permissions?.length) {
      return requiredPermissions.some((permission) => user.permissions.includes(permission));
    } else {
      return false;
    }
  };

  // display none class
  const displayNone = (permissions) => {
    return !hasPermission(permissions) ? "d-none" : "";
  };

  return {
    isAuth,
    hasPermission,
    displayNone
  };
}

export function ProtectedRoute({ children, requiredPermissions }) {
  const { isAuth, hasPermission } = useHasPermission();

  if (!isAuth) {
    return <Navigate to="/login" />;
  }

  if (!hasPermission(requiredPermissions)) {
    // Redirect the user to a different page or show an error message
    return <Navigate to="/unauthorized" />;
  }

  return children;
}

export const routesConfig = [
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true,
        element: <DasboardPage />,
        permissions: ["dashboard:view"]
      },
      {
        path: "dashboard",
        element: <DasboardPage />,
        permissions: ["dashboard:view"]
      },
      {
        path: "stock-list",
        children: [
          { index: true, element: <StockListPage />, permissions: ["stock-list:view"] },
          {
            path: "add",
            element: <StockListAddPage />,
            permissions: ["stock-list:create"]
          },
          {
            path: ":sku/edit",
            element: <StockListEditPage />,
            permissions: ["stock-list:update"]
          },
          {
            path: ":sku/view",
            element: <StockListViewPage />,
            permissions: ["stock-list:view"]
          },
          {
            path: "inventory/add",
            element: <VehicleInventoryAddPage />,
            permissions: ["stock-list-inventory:create"]
          },
          {
            path: "inventory/:caid/edit",
            element: <VehicleInventoryEditPage />,
            permissions: ["stock-list-inventory:update"]
          },
          {
            path: "showroom-works/add",
            element: <VehicleShowroomWorksAddPage />,
            permissions: ["stock-list-showroom-works:create"]
          },
          {
            path: "showroom-works/:caid/edit",
            element: <VehicleShowroomWorksEditPage />,
            permissions: ["stock-list-showroom-works:update"]
          }
        ]
      },
      {
        path: "quotation-list",
        children: [
          { index: true, element: <QuotationListPage />, permissions: ["quotation-list:view"] },
          {
            path: "add",
            element: <QuotationAddPage />,
            permissions: ["quotation-list:create"]
          },
          {
            path: ":caid/edit",
            element: <QuotationEditPage />,
            permissions: ["quotation-list:update"]
          }
        ]
      },
      {
        path: "money-receipt-list",
        children: [
          {
            index: true,
            element: <MoneyReceiptListPage />,
            permissions: ["money-receipt-list:view"]
          },
          {
            path: "add",
            element: <MoneyReceiptAddPage />,
            permissions: ["money-receipt-list:create"]
          },
          {
            path: ":caid/edit",
            element: <MoneyReceiptEditPage />,
            permissions: ["money-receipt-list:update"]
          },
          {
            path: ":caid/view",
            element: <MoneyReceiptViewPage />,
            permissions: ["money-receipt-list:view"]
          }
        ]
      },
      {
        path: "customer",
        children: [
          { index: true, element: <CustomerPage />, permissions: ["customer:view"] },
          {
            path: ":customer_id/view",
            element: <CustomerProfilePage />,
            permissions: ["customer-profile:view"]
          }
        ]
      },
      {
        path: "car",
        children: [
          { index: true, element: <SettingsPage name="Car" />, permissions: ["car:view"] },
          { path: "car-info", element: <CarInfoPage />, permissions: ["car-info:view"] },
          {
            path: "car-info-type",
            element: <CarInfoTypePage />,
            permissions: ["car-info-type:view"]
          }
        ]
      },
      { path: "pages", element: <Pages />, permissions: ["pages:view"] },
      {
        path: "user/:username/profile",
        element: <ProfilePage />,
        permissions: ["profile:view"]
      },
      {
        path: "settings",
        children: [
          { index: true, element: <SettingsPage />, permissions: ["settings:view"] },
          { path: "user", element: <UserPage />, permissions: ["user:view"] },
          { path: "role", element: <RolePage />, permissions: ["app-role:view"] },
          {
            path: "role/:slug",
            element: <RolePermissionPage />,
            permissions: ["role-manager:view"]
          },
          { path: "module", element: <ModulePage />, permissions: ["app-module:view"] },
          { path: "permission", element: <PermissionPage />, permissions: ["app-permission:view"] }
        ]
      },
      { path: "unauthorized", element: <UnauthorizedPage /> }
    ]
  },
  { path: "login", element: <LoginPage /> }
];

// get route state
export function useGetRouteState() {
  const location = useLocation();
  
  return location.state || {};
}
